/**
 * A foto grande, aberta por cima da tela — e SÓ quando alguém pede.
 *
 * É a segunda metade do desenho que o Lucas ditou (04/08): miniaturas
 * desvinculadas das imagens grandes. A miniatura viaja embutida no JSON e
 * aparece com a lista; a grande só é buscada no clique — *"se ele quer ver
 * grande ele aceita esperar mais um pouco"*.
 *
 * O carregamento mostra a MINIATURA AMPLIADA enquanto a grande chega: borrada,
 * mas instantânea — a pessoa vê "é essa foto" no primeiro quadro, e a nitidez
 * chega por cima. Esperar sobre um quadrado cinza parece travado; esperar
 * sobre a própria foto parece foco ajustando.
 *
 * A grande vem por `api.fotoAutenticada` — o MESMO canal do resto da API, com
 * o cabeçalho de autorização — e não por `<img src>` com cookie. O cookie era
 * o único elo que só as fotos tinham, e foi o elo que caiu em produção
 * (04/08). Aqui, se a API está de pé, a foto vem; e se um tropeço de rede
 * derrubar, há um botão de tentar de novo em vez de um beco.
 *
 * GALERIA (04/08, segunda rodada): item pode ter mais de uma foto, e quem
 * amplia quer ver as outras — a capa não mostra a bitola, o verso, o conector.
 * Se quem abriu informa o item (`data-fotos-de`), as demais fotos são buscadas
 * e aparecem as setas (‹ › e ←/→ no teclado) com o contador, como era no
 * sistema antigo. Com uma foto só, nada muda.
 */

import { api } from '../api.js';

/**
 * Uma janela por vez. Sem esta trava, dois cliques (ou um clique ouvido por
 * dois ouvintes — o bug do "fechar três vezes" de 04/08) empilham janelas
 * idênticas, e cada fechada revela outra igual embaixo: parece que o botão
 * não funciona.
 */
let fecharAberta: (() => void) | null = null;

interface FotoDaGaleria {
  grande: string;
  miniatura: string | null;
}

export function abrirFotoGrande(
  urlGrande: string,
  miniatura?: string | null,
  /** Código do item — com ele, as OUTRAS fotos do item entram na navegação. */
  galeriaDe?: string,
): void {
  fecharAberta?.();

  const fundo = document.createElement('div');
  fundo.className = 'foto-grande';
  fundo.innerHTML = `
    <figure>
      <img class="fg-previa" alt="" hidden>
      <img class="fg-cheia" alt="Foto ampliada">
      <div class="fg-carregando" hidden></div>
    </figure>
    <button class="fg-ante" aria-label="Foto anterior" hidden>‹</button>
    <button class="fg-prox" aria-label="Próxima foto" hidden>›</button>
    <div class="fg-conta" hidden></div>
    <button class="fg-fechar" aria-label="Fechar">✕</button>`;
  document.body.appendChild(fundo);

  const previa = fundo.querySelector<HTMLImageElement>('.fg-previa')!;
  const cheia = fundo.querySelector<HTMLImageElement>('.fg-cheia')!;
  const aviso = fundo.querySelector<HTMLElement>('.fg-carregando')!;
  const ante = fundo.querySelector<HTMLButtonElement>('.fg-ante')!;
  const prox = fundo.querySelector<HTMLButtonElement>('.fg-prox')!;
  const conta = fundo.querySelector<HTMLElement>('.fg-conta')!;

  let fotos: FotoDaGaleria[] = [{ grande: urlGrande, miniatura: miniatura ?? null }];
  let indice = 0;

  cheia.addEventListener('load', () => {
    fundo.classList.add('pronta');
    aviso.hidden = true;
  });

  const mostrar = (i: number): void => {
    indice = (i + fotos.length) % fotos.length;
    const f = fotos[indice]!;
    if (fotos.length > 1) conta.textContent = `${indice + 1} / ${fotos.length}`;

    fundo.classList.remove('pronta');
    if (f.miniatura) {
      previa.src = f.miniatura;
      previa.hidden = false;
    } else {
      previa.hidden = true;
    }
    aviso.hidden = false;
    aviso.textContent = 'carregando a foto em alta…';

    const pedida = indice;
    api
      .fotoAutenticada(f.grande)
      .then((local) => {
        if (indice !== pedida) return; // já navegou para outra
        cheia.src = local;
      })
      .catch(() => {
        if (indice !== pedida) return;
        // Beco sem saída, não: a falha aqui é quase sempre passageira (rede da
        // obra, servidor acordando) — merece um botão, não um ponto final.
        aviso.innerHTML = `Não consegui carregar a foto em alta.
          <button class="btn btn-claro fg-denovo">Tentar de novo</button>`;
        aviso.querySelector('.fg-denovo')!.addEventListener('click', (e) => {
          e.stopPropagation(); // tentar de novo não é fechar
          mostrar(indice);
        });
      });
  };
  mostrar(0);

  // As outras fotos do item chegam DEPOIS de a primeira já estar na tela — a
  // galeria nunca atrasa o clique. Falhou a busca? Fica a foto única, sem
  // aviso: as setas são um extra, não a função.
  if (galeriaDe) {
    void api
      .get<{ fotos: { url: string | null; miniatura: string | null }[] }>(
        `/api/itens/${encodeURIComponent(galeriaDe)}/fotos`,
      )
      .then((r) => {
        const todas = r.fotos
          .map((f) => ({ grande: api.foto(f.url), miniatura: f.miniatura }))
          .filter((f): f is FotoDaGaleria => !!f.grande);
        if (todas.length < 2 || !fundo.isConnected) return;
        fotos = todas;
        const daCapa = fotos.findIndex((f) => f.grande === urlGrande);
        indice = daCapa >= 0 ? daCapa : 0;
        conta.textContent = `${indice + 1} / ${fotos.length}`;
        ante.hidden = prox.hidden = conta.hidden = false;
      })
      .catch(() => {});
  }

  ante.addEventListener('click', (e) => {
    e.stopPropagation();
    mostrar(indice - 1);
  });
  prox.addEventListener('click', (e) => {
    e.stopPropagation();
    mostrar(indice + 1);
  });

  const fechar = () => {
    fundo.remove();
    document.removeEventListener('keydown', porTecla);
    if (fecharAberta === fechar) fecharAberta = null;
  };
  const porTecla = (e: KeyboardEvent) => {
    if (e.key === 'Escape') fechar();
    if (fotos.length > 1 && e.key === 'ArrowLeft') mostrar(indice - 1);
    if (fotos.length > 1 && e.key === 'ArrowRight') mostrar(indice + 1);
  };
  fundo.addEventListener('click', fechar);
  document.addEventListener('keydown', porTecla);
  fecharAberta = fechar;
}

/**
 * Liga o comportamento numa raiz inteira: qualquer `<img data-grande>` vira
 * clicável — e `data-fotos-de` (código do item) liga a navegação da galeria.
 * Delegação de evento — as telas redesenham o HTML o tempo todo, e ouvinte
 * por imagem morreria em cada redesenho.
 *
 * É instalado UMA vez, no `main.ts`, sobre o `<body>` — não por tela. A versão
 * por tela instalava um ouvinte novo a cada visita à mesma tela (a raiz
 * persiste entre visitas), e três visitas = três janelas empilhadas por
 * clique. A trava abaixo segura qualquer chamada repetida que sobre.
 */
const raizesLigadas = new WeakSet<HTMLElement>();

export function instalarFotoGrande(raiz: HTMLElement): void {
  if (raizesLigadas.has(raiz)) return;
  raizesLigadas.add(raiz);
  raiz.addEventListener('click', (e) => {
    const img = (e.target as HTMLElement).closest<HTMLImageElement>('img[data-grande]');
    if (!img) return;
    e.preventDefault();
    e.stopPropagation();
    abrirFotoGrande(
      img.dataset['grande']!,
      img.src.startsWith('data:') ? img.src : null,
      img.dataset['fotosDe'],
    );
  });
}
